/**
 * Max quantity per order (variant metafield custom.max_quantity)
 * Wraps fetch() for /cart/add, /cart/change and /cart/update so every
 * add-to-cart path (product page, quick add, upsells, cart +/–) respects
 * the limit, counting what is already in the cart. Limits come from
 * snippets/max-quantity-data.liquid. Theme-level only – checkout does not enforce it.
 */
(function () {
	if (window.__maxQtyInstalled) return;
	window.__maxQtyInstalled = true;

	const cfg = window.maxQtyConfig || {};
	const origFetch = window.fetch.bind(window);
	const CART_RE = /\/cart\/(add|change|update)(\.js)?\/?$/;

	/* ---------- limits (variant id -> max, 0 = none) ----------
	   Rendered by snippets/max-quantity-data.liquid on the product page,
	   product cards and cart lines. */
	function getLimits(ids) {
		const known = {};
		document.querySelectorAll('script[data-max-qty]').forEach((el) => {
			try { Object.assign(known, JSON.parse(el.textContent)); } catch (e) {}
		});
		const out = {};
		[...new Set(ids.map(String))].forEach((id) => { out[id] = parseInt(known[id], 10) || 0; });
		return out;
	}

	async function getCart() {
		const res = await origFetch(cfg.cartJs || '/cart.js', { headers: { Accept: 'application/json' } });
		return res.json();
	}

	function inCart(cart, variantId, exceptKey) {
		return cart.items
			.filter((i) => String(i.variant_id) === String(variantId) && i.key !== exceptKey)
			.reduce((sum, i) => sum + i.quantity, 0);
	}

	/* ---------- message ---------- */
	function message(limit) {
		return (cfg.reached || 'You can buy up to %COUNT% of this item per order.').replace('%COUNT%', limit);
	}
	let toastTimer;
	function toast(text) {
		// Product page form shows the theme's own error for blocked adds
		let el = document.querySelector('.max-qty-toast');
		if (!el) {
			el = document.createElement('div');
			el.className = 'max-qty-toast';
			el.setAttribute('role', 'status');
			document.body.appendChild(el);
		}
		el.textContent = text;
		el.hidden = false;
		clearTimeout(toastTimer);
		toastTimer = setTimeout(() => { el.hidden = true; }, 4500);
	}

	/* ---------- body helpers ---------- */
	function readBody(body) {
		if (body instanceof FormData) return { type: 'form', data: body };
		if (body instanceof URLSearchParams) return { type: 'params', data: body };
		if (typeof body === 'string') {
			try { return { type: 'json', data: JSON.parse(body) }; } catch (e) {}
			return { type: 'params', data: new URLSearchParams(body), wasString: true };
		}
		return null;
	}
	function writeBody(parsed) {
		if (parsed.type === 'json') return JSON.stringify(parsed.data);
		if (parsed.type === 'params' && parsed.wasString) return parsed.data.toString();
		return parsed.data;
	}

	/* ---------- /cart/add ---------- */
	async function handleAdd(parsed) {
		// Normalise to [{ id, quantity, set(q) }]
		const items = [];
		if (parsed.type === 'json') {
			const d = parsed.data;
			if (Array.isArray(d.items)) d.items.forEach((it) => items.push({ id: it.id, quantity: parseInt(it.quantity || 1, 10), set: (q) => { it.quantity = q; }, drop: () => { it.__drop = true; } }));
			else if (d.id) items.push({ id: d.id, quantity: parseInt(d.quantity || 1, 10), set: (q) => { d.quantity = q; }, drop: () => { d.__drop = true; } });
		} else {
			const f = parsed.data;
			if (f.get('id')) {
				items.push({ id: f.get('id'), quantity: parseInt(f.get('quantity') || 1, 10), set: (q) => f.set('quantity', q), drop: () => { f.__drop = true; } });
			} else {
				for (let i = 0; f.get(`items[${i}][id]`); i++) {
					const idx = i;
					items.push({ id: f.get(`items[${idx}][id]`), quantity: parseInt(f.get(`items[${idx}][quantity]`) || 1, 10), set: (q) => f.set(`items[${idx}][quantity]`, q), drop: () => f.set(`items[${idx}][quantity]`, 0) });
				}
			}
		}
		if (!items.length) return { ok: true };

		const lim = getLimits(items.map((i) => i.id));
		if (!items.some((i) => lim[String(i.id)])) return { ok: true };

		const cart = await getCart();
		const used = {};
		let hit = 0;
		let added = 0;
		items.forEach((it) => {
			const max = lim[String(it.id)];
			if (!max) { added++; return; }
			const already = (used[it.id] || 0) + inCart(cart, it.id);
			const allowed = Math.max(0, max - already);
			if (it.quantity > allowed) {
				hit = max;
				if (allowed > 0) { it.set(allowed); it.quantity = allowed; } else { it.drop(); it.quantity = 0; }
			}
			used[it.id] = (used[it.id] || 0) + it.quantity;
			if (it.quantity > 0) added++;
		});

		// Remove dropped JSON items
		if (parsed.type === 'json') {
			const d = parsed.data;
			if (Array.isArray(d.items)) d.items = d.items.filter((it) => !it.__drop);
			if (d.__drop || (Array.isArray(d.items) && !d.items.length)) return { blocked: true, limit: hit };
		} else if (parsed.data.__drop) {
			return { blocked: true, limit: hit };
		}
		if (!added) return { blocked: true, limit: hit };
		return { ok: true, limit: hit };
	}

	/* ---------- /cart/change ---------- */
	async function handleChange(parsed) {
		const d = parsed.type === 'json' ? parsed.data : null;
		const get = (k) => (d ? d[k] : parsed.data.get(k));
		const set = (k, v) => (d ? (d[k] = v) : parsed.data.set(k, v));
		const quantity = parseInt(get('quantity'), 10);
		if (!(quantity > 0)) return { ok: true };

		const cart = await getCart();
		let item = null;
		const line = get('line');
		const id = get('id');
		if (line) item = cart.items[parseInt(line, 10) - 1];
		else if (id) item = cart.items.find((i) => i.key === String(id)) || cart.items.find((i) => String(i.variant_id) === String(id));
		if (!item || quantity <= item.quantity) return { ok: true };

		const lim = getLimits([item.variant_id]);
		const max = lim[String(item.variant_id)];
		if (!max) return { ok: true };
		const allowed = Math.max(0, max - inCart(cart, item.variant_id, item.key));
		if (quantity > allowed) {
			// never go below what is already there (+ shouldn't remove a line)
			set('quantity', Math.max(allowed, item.quantity));
			return { ok: true, limit: max };
		}
		return { ok: true };
	}

	/* ---------- /cart/update ---------- */
	async function handleUpdate(parsed) {
		const cart = await getCart();
		// Collect requested quantities by line key
		const wanted = {}; // key -> qty
		const setters = {};
		if (parsed.type === 'json' && parsed.data.updates) {
			const u = parsed.data.updates;
			if (Array.isArray(u)) {
				u.forEach((q, idx) => { const it = cart.items[idx]; if (it) { wanted[it.key] = parseInt(q, 10); setters[it.key] = (v) => { u[idx] = v; }; } });
			} else {
				Object.keys(u).forEach((k) => {
					const it = cart.items.find((i) => i.key === k) || cart.items.find((i) => String(i.variant_id) === k);
					if (it) { wanted[it.key] = parseInt(u[k], 10); setters[it.key] = (v) => { u[k] = v; }; }
				});
			}
		} else if (parsed.type !== 'json') {
			const f = parsed.data;
			const arr = f.getAll('updates[]');
			arr.forEach((q, idx) => { const it = cart.items[idx]; if (it) { wanted[it.key] = parseInt(q, 10); } });
			if (arr.length) {
				Object.keys(wanted).forEach((k) => {
					setters[k] = (v) => {
						const all = f.getAll('updates[]');
						const idx = cart.items.findIndex((i) => i.key === k);
						all[idx] = v;
						f.delete('updates[]');
						all.forEach((x) => f.append('updates[]', x));
					};
				});
			}
		}
		const keys = Object.keys(wanted);
		if (!keys.length) return { ok: true };

		const lim = getLimits(cart.items.map((i) => i.variant_id));
		let hit = 0;
		const byVariant = {};
		cart.items.forEach((i) => {
			const q = i.key in wanted ? wanted[i.key] : i.quantity;
			(byVariant[i.variant_id] = byVariant[i.variant_id] || []).push({ key: i.key, q, old: i.quantity });
		});
		Object.keys(byVariant).forEach((vid) => {
			const max = lim[String(vid)];
			if (!max) return;
			let total = byVariant[vid].reduce((s, l) => s + l.q, 0);
			if (total <= max) return;
			hit = max;
			// Reduce the lines that were increased, last first
			[...byVariant[vid]].reverse().forEach((l) => {
				if (total <= max || !(l.key in wanted) || !setters[l.key]) return;
				const reduce = Math.min(Math.max(0, l.q - l.old), total - max); // only undo increases
				setters[l.key](l.q - reduce);
				total -= reduce;
			});
		});
		return { ok: true, limit: hit };
	}

	/* ---------- fetch wrapper ---------- */
	window.fetch = async function (input, init) {
		try {
			const url = typeof input === 'string' ? input : input instanceof URL ? input.href : null;
			const method = ((init && init.method) || 'GET').toUpperCase();
			if (!url || method !== 'POST' || !init || !init.body) return origFetch(input, init);
			const path = new URL(url, location.origin).pathname;
			const m = path.match(CART_RE);
			if (!m) return origFetch(input, init);

			const parsed = readBody(init.body);
			if (!parsed) return origFetch(input, init);

			const result = m[1] === 'add' ? await handleAdd(parsed)
				: m[1] === 'change' ? await handleChange(parsed)
				: await handleUpdate(parsed);

			const fromProductForm = document.activeElement && document.activeElement.closest && document.activeElement.closest('product-form');

			if (result.blocked) {
				const text = message(result.limit);
				if (!fromProductForm) toast(text);
				return new Response(JSON.stringify({ status: 422, message: 'Cart Error', description: text }), {
					status: 422,
					headers: { 'Content-Type': 'application/json' }
				});
			}
			if (result.limit) toast(message(result.limit));
			return origFetch(input, Object.assign({}, init, { body: writeBody(parsed) }));
		} catch (e) {
			return origFetch(input, init); // never break the cart
		}
	};
})();
